import {
  Autocomplete,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
} from "@mui/material";
import * as React from "react";

import { Button } from "@/shared/foundation/primitives";

import styles from "./FilterPanel.module.css";

export interface FilterOption {
  label: string;
  id: string;
}

interface FilterPanelContextValue {
  onSearch: () => void;
}

const FilterPanelContext = React.createContext<FilterPanelContextValue | null>(
  null,
);

/**
 * Returns a keydown handler that triggers the enclosing {@link FilterPanel}'s
 * onSearch when Enter is pressed. Used by the built-in filter inputs in this
 * file; intentionally not exported because the hook is a no-op outside a
 * ``FilterPanel`` and we'd rather force callers to live inside the panel
 * than ship a "silently does nothing" hook to other modules.
 */
function useFilterPanelEnter(): (e: React.KeyboardEvent) => void {
  const ctx = React.useContext(FilterPanelContext);
  if (ctx === null) {
    // Throw rather than no-op: a hook intended exclusively for use inside
    // ``FilterPanel`` should fail loudly when wired up elsewhere instead of
    // quietly making Enter do nothing.
    throw new Error("useFilterPanelEnter must be used inside a <FilterPanel>");
  }
  return React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        ctx.onSearch();
      }
    },
    [ctx],
  );
}

interface FilterPanelProps {
  children: React.ReactNode;
  onSearch: () => void;
  onClear: () => void;
  isLoading: boolean;
}

/**
 * Container for an admin filter form: lays out fields in a wrapping row and
 * renders Search + Clear buttons at the end. Children use the sibling filter
 * components (OrgFilter, UserFilter, SessionIdFilter) or drop in their own
 * MUI inputs; Enter-to-search is wired for built-in filters automatically
 * and available to custom inputs via {@link useFilterPanelEnter}.
 */
export function FilterPanel({
  children,
  onSearch,
  onClear,
  isLoading,
}: FilterPanelProps) {
  const ctxValue = React.useMemo<FilterPanelContextValue>(
    () => ({ onSearch }),
    [onSearch],
  );
  return (
    <FilterPanelContext.Provider value={ctxValue}>
      <div className={styles.filtersContainer}>
        {children}
        <div className={styles.filterActions}>
          <Button
            variant="contained"
            size="small"
            onClick={onSearch}
            disabled={isLoading}
          >
            Search
          </Button>
          <Button variant="outlined" size="small" onClick={onClear}>
            Clear
          </Button>
        </div>
      </div>
    </FilterPanelContext.Provider>
  );
}

interface OptionAutocompleteProps {
  label: string;
  placeholder?: string;
  value: FilterOption | null;
  onChange: (value: FilterOption | null) => void;
  options: FilterOption[];
  loading: boolean;
  PaperComponent?: React.JSXElementConstructor<
    React.HTMLAttributes<HTMLElement>
  >;
}

function OptionAutocomplete({
  label,
  placeholder,
  value,
  onChange,
  options,
  loading,
  PaperComponent,
}: OptionAutocompleteProps) {
  const handleKeyDown = useFilterPanelEnter();
  return (
    <Autocomplete
      className={styles.filterInput}
      options={options}
      loading={loading}
      value={value}
      onChange={(_e, newValue) => onChange(newValue)}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      PaperComponent={PaperComponent}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
        />
      )}
      renderOption={(props, option) => (
        <li key={option.id} {...props}>
          <span className={styles.optionLabel}>{option.label}</span>
        </li>
      )}
    />
  );
}

interface OrgFilterProps {
  value: FilterOption | null;
  onChange: (value: FilterOption | null) => void;
  options: FilterOption[];
  loading: boolean;
}

export function OrgFilter(props: OrgFilterProps) {
  return (
    <OptionAutocomplete
      label="Organization"
      placeholder="Search by name or ID..."
      {...props}
    />
  );
}

interface UserFilterProps {
  value: FilterOption | null;
  onChange: (value: FilterOption | null) => void;
  options: FilterOption[];
  loading: boolean;
  /**
   * When provided, a "Customer only" switch is rendered inside the dropdown's
   * popup. Flipping it on hides Roboto staff (``@roboto.ai``) and service
   * users (``.srv`` suffix) from the option list, and surfaces the active
   * mode in the input's placeholder so it's visible while the popup is shut.
   * Omit this pair on screens where the staff/customer split isn't useful.
   */
  customerOnly?: boolean;
  onCustomerOnlyChange?: (next: boolean) => void;
}

export function UserFilter({
  value,
  onChange,
  options,
  loading,
  customerOnly,
  onCustomerOnlyChange,
}: UserFilterProps) {
  const showCustomerToggle = onCustomerOnlyChange !== undefined;
  const isCustomerOnly = customerOnly === true;

  const visibleOptions = React.useMemo(() => {
    if (!showCustomerToggle || !isCustomerOnly) {
      return options;
    }
    // Anchor the staff check on ``@`` so a customer at e.g. ``notroboto.ai``
    // isn't accidentally hidden alongside actual Roboto employees.
    return options.filter(
      (u) => !u.id.endsWith("@roboto.ai") && !u.id.endsWith(".srv"),
    );
  }, [options, showCustomerToggle, isCustomerOnly]);

  const PaperComponent = React.useMemo(() => {
    if (!showCustomerToggle) {
      return undefined;
    }
    return function CustomerTogglePaper(
      props: React.HTMLAttributes<HTMLElement>,
    ) {
      const { children, ...rest } = props;
      return (
        <Paper {...rest}>
          <div
            className={styles.customerToggleHeader}
            // Autocomplete commits a blur on mousedown anywhere outside the
            // input; without preventDefault the popup would close mid-click
            // and the switch wouldn't toggle.
            onMouseDown={(e) => e.preventDefault()}
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isCustomerOnly}
                  onChange={(_e, checked) => onCustomerOnlyChange?.(checked)}
                />
              }
              label="Customer only"
            />
          </div>
          {children}
        </Paper>
      );
    };
  }, [showCustomerToggle, isCustomerOnly, onCustomerOnlyChange]);

  const placeholder =
    showCustomerToggle && isCustomerOnly
      ? "Customer only — search by name or email..."
      : "Search by name or email...";

  return (
    <OptionAutocomplete
      label="User"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      options={visibleOptions}
      loading={loading}
      PaperComponent={PaperComponent}
    />
  );
}

interface SessionIdFilterProps {
  value: string;
  onChange: (value: string) => void;
  /** Visible label. Defaults to ``"Thread ID"``. */
  label?: string;
  placeholder?: string;
}

export function SessionIdFilter({
  value,
  onChange,
  label = "Thread ID",
  placeholder = "Thread ID...",
}: SessionIdFilterProps) {
  const handleKeyDown = useFilterPanelEnter();
  return (
    <TextField
      className={styles.filterInput}
      label={label}
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
    />
  );
}

/**
 * Class name to apply to a caller-owned TextField/Select that should pick up
 * the shared filter-input sizing (flex: 1, min-width: 180px).
 */
// eslint-disable-next-line react-refresh/only-export-components
export const filterInputClassName = styles.filterInput;
